import { useState } from 'react'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'
import { GetExpensablesFromGroupDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { Owner } from 'modules/expenses/components/expenses/DataTable/_Owner'
import type {
  Expensable,
  ExpensableOwner,
} from 'modules/expenses/components/expenses/DataTable/types'
import type { Expensable as HookExpensable } from 'modules/expenses/types/Expensable'

import { ExpensableCard } from './ExpensableCard'
import { WarningModal } from './WarningModal'

interface AddToPaymentModalExpensableProps {
  onSubmit: (ids: number[], dueDate: Date) => void
  onClose: () => void
  expensables: (Expensable | HookExpensable)[]
  groupId?: never
}

interface AddToPaymentModalGroupProps {
  onSubmit: (ids: number[], dueDate: Date) => void
  onClose: () => void
  expensables?: never
  groupId: number
}

type AddToPaymentModalProps = AddToPaymentModalExpensableProps | AddToPaymentModalGroupProps

function hasOwner(
  expensable: Expensable | HookExpensable
): expensable is Expensable & { owner: { bankNumber?: string } } {
  return !!expensable.owner
}

function getResourceTypeTitle(resourceType: string) {
  const lowerCaseResourceType = resourceType.toLowerCase()
  switch (lowerCaseResourceType) {
    case 'expense':
      return i18n.t('expenses_v2.list.actions.modal.add_to_payment.expense_types.expense')
    default:
      return ''
  }
}

const AddToPaymentModal = ({ onSubmit, onClose, expensables, groupId }: AddToPaymentModalProps) => {
  const { access } = useCurrentSession()
  const { data, loading } = useQuery(GetExpensablesFromGroupDocument, {
    variables: {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      groupId: groupId!,
    },
    skip: !groupId,
  })

  const form = useForm<{ dueDate?: string }>({
    defaultValues: {},
    validate: (values) => {
      const errors: Record<string, string> = {}

      if (!values.dueDate) {
        errors.dueDate = i18n.t('expenses_v2.list.actions.modal.add_to_payment.reimburse.error')
      }

      return errors
    },
    onSubmit: async (values) => {
      form.validateForm(values)
      const dueDate = values.dueDate
      if (form.isValid && dueDate && (groupId || expensablesWithIBAN.length > 0)) {
        onClose()
        onSubmit(groupId ? [groupId] : expensablesWithIBAN.map((exp) => exp.id), new Date(dueDate))
        return {
          data: {},
          success: true,
        }
      }
      return {
        errors: [
          {
            field: 'dueDate',
            message: i18n.t('expenses_v2.list.actions.modal.add_to_payment.reimburse.error'),
            type: 'validation',
          },
        ],
        success: false,
      }
    },
  })

  const handleCancel = () => {
    onClose()
  }

  const expensablesWithOwner = expensables?.filter(hasOwner) ?? []
  const expensablesWithoutIBAN = expensablesWithOwner.filter(
    (expensable) =>
      !expensable.owner.bankNumber ||
      !expensable.owner.bankNumberFormat ||
      expensable.owner.bankNumberFormat !== 'iban'
  )

  const expensablesWithIBAN = expensablesWithOwner.filter(
    (expensable) => expensable.owner.bankNumber && expensable.owner.bankNumberFormat === 'iban'
  )

  const ownersInitialValue: Record<number, ExpensableOwner> = {}
  const owners = expensablesWithOwner.reduce((acc, expensable) => {
    acc[expensable.owner.id] = expensable.owner
    return acc
  }, ownersInitialValue)

  const failedExpensesInitialValue: Record<number, number> = {}
  const failedExpensesByOwner = expensablesWithoutIBAN.reduce((acc, expensable) => {
    acc[expensable.owner.id] = (acc[expensable.owner.id] ?? 0) + 1
    return acc
  }, failedExpensesInitialValue)

  const totalFailedOwners = Object.keys(failedExpensesByOwner).length

  const allExpensesAreInvalid = expensablesWithoutIBAN.length === expensablesWithOwner.length
  const allExpensesAreValid = expensablesWithIBAN.length === expensablesWithOwner.length
  const showWarning = expensablesWithIBAN.length !== expensablesWithOwner.length
  const [showingWarning, setShowingWarning] = useState(showWarning)
  const currency =
    data?.expenses?.expensableGroup?.reimbursableCurrency ??
    expensables?.[0]?.reimbursableCurrency ??
    null

  const handleSkipAndContinue = () => {
    setShowingWarning(false)
  }

  if (showingWarning) {
    return (
      <WarningModal
        allExpensesAreInvalid={allExpensesAreInvalid}
        totalFailedEmployees={totalFailedOwners}
        onCancel={handleCancel}
        onClose={onClose}
        onSkipAndContinue={handleSkipAndContinue}
        hashIdEmployees={owners}
        hashEmployeeIdAndExpensesCount={failedExpensesByOwner}
      />
    )
  }

  const resourceTypes =
    (data?.expenses.expensablesConnection.aggregate?.terms?.type?.values ??
    expensablesWithIBAN.length > 0)
      ? ['expense']
      : []

  const resourceTypesCount =
    data?.expenses.expensablesConnection.aggregate?.terms?.type?.count ??
    (expensablesWithIBAN.length > 0 ? [expensablesWithIBAN.length] : [])
  const resourceTypesSum = data?.expenses.expensablesConnection.aggregate?.terms?.type?.sum ?? {
    reimbursableAmount:
      expensablesWithIBAN.length > 0
        ? [expensablesWithIBAN.reduce((acc, exp) => acc + +exp.reimbursableAmount, 0)]
        : [],
  }

  const expensableList = (expensablesWithIBAN ? [expensablesWithIBAN] : []).filter(
    (el) => !!el && el.length > 0
  )

  return (
    <Modal
      illustration="list-document"
      title={i18n.t('expenses_v2.list.actions.modal.add_to_payment.reimburse.title')}
      description={i18n.tp('expenses_v2.list.actions.modal.add_to_payment.reimburse.description', {
        count: expensablesWithIBAN.length,
      })}
      footer={
        !loading && (
          <>
            <Button type="secondary" onClick={handleCancel}>
              {i18n.t('expenses_v2.list.actions.modal.add_to_payment.buttons.cancel')}
            </Button>
            <Button onClick={form.submitForm}>
              {allExpensesAreValid
                ? i18n.t('common.submit')
                : i18n.tp(
                    'expenses_v2.list.actions.modal.add_to_payment.buttons.reimburse_n_expenses',
                    {
                      count: expensablesWithIBAN.length,
                    }
                  )}
            </Button>
          </>
        )
      }
      onClose={onClose}
    >
      {loading && <Loading />}
      {!loading && (
        <Box flexDirection="column" gap="s8">
          {resourceTypes.map((resourceType, index) => (
            <ExpensableCard
              key={resourceType}
              title={getResourceTypeTitle(resourceType)}
              amountCents={resourceTypesSum?.reimbursableAmount?.[index] ?? 0}
              hideDetail={!!groupId}
              total={resourceTypesCount?.[index] ?? 0}
              currency={currency ?? ''}
              locale={access.locale}
            >
              <List>
                {(expensableList[index] ?? []).map((el) => (
                  <List.Item key={el.id}>
                    <Box
                      width="full"
                      flexDirection="row"
                      justifyContent="spaceBetween"
                      alignItems="center"
                    >
                      <Owner owner={el.owner} />
                      <Text.Normal>
                        {formattedAmount({
                          amountCents: +(el.reimbursableAmount ?? el.amount),
                          currency: el.reimbursableCurrency ?? el.currency,
                          locale: access.locale,
                        })}
                      </Text.Normal>
                    </Box>
                  </List.Item>
                ))}
              </List>
            </ExpensableCard>
          ))}
          <Box paddingTop="s16">
            <DayPicker
              label={i18n.t('expenses_v2.list.actions.modal.add_to_payment.reimburse.label')}
              onChange={(value) => form.setFieldValue('dueDate', value)}
              value={form.values.dueDate}
              error={form.errors.dueDate}
              mandatory
              minDate={moment()}
              format="ui_long_with_year"
              valueFormat="backend"
            />
          </Box>
        </Box>
      )}
    </Modal>
  )
}

export default observer(AddToPaymentModal)
